import { Button } from '~/components/buttons/Button/Button';
import { resetSatelliteSyncAttempts } from './satelliteSyncGuard';

type SatelliteAuthUnavailableProps = {
  /**
   * Retry handler. Defaults to the satellite (HLX) behaviour: clear that side's
   * bounce counter and reload. [MCV-1789] The primary (MCV) side passes its own,
   * because it counts on a different guard.
   */
  onRetry?: () => void;
  /** Replaces the first paragraph, for a host where the default wording reads wrong. */
  body?: string;
  /** Optional second action that leaves the page by a full navigation. */
  secondaryLink?: { label: string; href: string };
};

/**
 * Shown on the satellite (HLX) host when the auth hand-off to the primary keeps
 * returning signed-out — the browser is blocking the cross-site session cookie
 * (common in private/incognito windows and in Safari, which restricts
 * cross-site tracking). Rendered instead of the infinite sign-in bounce so the
 * user has a clear, recoverable state. See satelliteSyncGuard for the breaker.
 *
 * [MCV-1789] The primary (MCV) host reuses it when a signed-in user's hand-off to
 * HealthcareLX keeps bouncing back, with its own retry, wording and a way out.
 */
export const SatelliteAuthUnavailable = ({
  onRetry = () => {
    resetSatelliteSyncAttempts();
    window.location.reload();
  },
  body = 'Your browser blocked the secure hand-off between our sign-in service and this site. This usually happens in private or incognito windows, and in browsers with stricter cross-site privacy protections.',
  secondaryLink,
}: SatelliteAuthUnavailableProps) => {
  return (
    // `min-h-screen` + vertical padding (not `h-screen`) so that on short or
    // zoomed viewports the panel grows downward and stays scrollable instead of
    // being clipped above the scroll origin when it is taller than the viewport.
    <div className="flex min-h-screen flex-col items-center justify-center bg-primary-t-950 px-6 py-12">
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        <h1 className="heading-large text-grey-100">We couldn&apos;t finish signing you in</h1>
        <p className="text-sm text-grey-400">{body}</p>
        <p className="text-sm text-grey-400">
          Please try again in a normal (non-private) window. If it keeps happening, signing in with
          a different browser such as Chrome resolves it.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button label="Try again" type="button" onClick={onRetry} />
          {secondaryLink && (
            // A native anchor on purpose: a full navigation, not a client-side route
            // change, so nothing from the bounced hand-off carries over. Styled to
            // match the Button `outline` variant at the same size.
            <a
              href={secondaryLink.href}
              className="body-text-sb inline-flex h-[50px] min-w-[126px] items-center justify-center rounded-control border border-primary-t-200 bg-transparent p-4 text-primary-50 hover:border-primary-t-100 hover:bg-primary-t-900 active:bg-primary-t-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-50"
            >
              {secondaryLink.label}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
